import { Injectable, inject } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { UiDialogComponent } from "@template-ui/components";
import { EzDialogData, IEzDialogComponent } from "@template-ui/types";
import { firstValueFrom } from "rxjs";

export interface WorkflowDraftConfirmationRequest {
  heading: string;
  message: string;
  confirmText: string;
  cancelText: string;
  tone?: "info" | "warning";
}

@Injectable({ providedIn: "root" })
export class WorkflowDraftConfirmationService {
  private readonly dialog = inject(MatDialog);

  confirm(request: WorkflowDraftConfirmationRequest): Promise<boolean> {
    const data: EzDialogData = {
      heading: request.heading,
      headerStyle: request.tone ?? "warning",
      // EzDialog supports trusted HTML; workflow confirmation messages are plain text.
      question: request.tone === "info" ? undefined : escapeDialogText(request.message),
      content: request.tone === "info" ? escapeDialogText(request.message) : undefined,
      contentAlignment: "left",
      useForm: false,
      config: [],
      formActions: [],
      buttons: [
        {
          text: request.cancelText,
          severity: "primary",
          outlined: false,
          defaultFocus: true,
          action: (ref: MatDialogRef<IEzDialogComponent>) => ref.close(false),
        },
        {
          text: request.confirmText,
          severity: request.tone === "info" ? "secondary" : "danger",
          outlined: true,
          action: (ref: MatDialogRef<IEzDialogComponent>) => ref.close(true),
        },
      ],
    };

    const dialogRef: MatDialogRef<UiDialogComponent> = this.dialog.open(UiDialogComponent, {
      width: "34rem",
      maxWidth: "calc(100vw - 4rem)",
      autoFocus: "first-tabbable",
      restoreFocus: true,
      disableClose: false,
      role: request.tone === "info" ? "dialog" : "alertdialog",
      ariaLabel: request.heading,
      data,
    });

    return firstValueFrom(dialogRef.afterClosed())
      .then((result) => result === true);
  }
}

function escapeDialogText(value: string): string {
  const entities: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  return value.replace(/[&<>"']/g, character => entities[character]);
}
